"use client";

// Модалка документа «Базы знаний» (среднего размера): эмодзи+заголовок, область
// (личное/командное), раздел, богатый редактор. Создание/правка/удаление.

import { useEffect, useRef, useState } from "react";
import { ВыборРаздела } from "@/components/kb/category-picker";
import { toast } from "@/lib/toast";
import { confirmDialog } from "@/components/ui/confirm";
import { Image as ImageIcon } from "lucide-react";
import { IconClose, IconSpinner, IconTrash } from "@/components/ui/icons";

import { api } from "@/lib/api";
import type { KbDocScope } from "@/lib/types";
import { RichEditor } from "@/components/kb/rich-editor";
import { AuthImage } from "@/components/ui/auth-image";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Z } from "@/lib/z";

const EMOJI_QUICK = ["📄","📁","📌","⭐","✅","📝","💡","🔥","📊","📈","🔒","🔑","👤","👥","🏢","💼","📦","🚀","🎯","⚙️","📅","💰","🛒","🤝","📞","✉️","🧩","🧠","🌟","⚡"];

// Позиция поповера РЯДОМ с триггером (справа; слева — если не влезает).
function besidePos(rect: DOMRect | null, width: number): React.CSSProperties {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const w = Math.min(width, vw - 16);
  const height = Math.min(Math.round(vh * 0.82), vh - 16);
  if (!rect) {
    const left = (vw - w) / 2;
    const top = Math.max(8, (vh - height) / 2);
    return { position: "fixed", left, top, width: w, height, zIndex: Z.MODAL + 1 };
  }
  let left = rect.right + 8;
  if (left + w > vw - 8) left = rect.left - w - 8;
  if (left < 8) left = Math.min(Math.max(8, rect.left), vw - w - 8);
  let top = rect.top;
  if (top + height > vh - 8) top = vh - 8 - height;
  if (top < 8) top = 8;
  return { position: "fixed", left, top, width: w, height, zIndex: Z.MODAL + 1 };
}

export function KbDocModal({ docId, presetScope = "personal", presetCategory = "", parentId, categories, anchor = null, onClose, onSaved }: {
  docId?: number;
  presetScope?: KbDocScope;
  presetCategory?: string;
  parentId?: number;
  categories: string[];
  anchor?: DOMRect | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isChild = !docId && parentId != null;  // создаём подраздел/документ внутри (область+раздел наследуются)
  const [pos] = useState<React.CSSProperties>(() => besidePos(anchor, 640));
  const [loading, setLoading] = useState(!!docId);
  const [id, setId] = useState<number | undefined>(docId);
  const [emoji, setEmoji] = useState("");
  const [iconUrl, setIconUrl] = useState("");
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState<KbDocScope>(presetScope);
  const [category, setCategory] = useState(presetCategory);
  const [content, setContent] = useState("");
  const [canEdit, setCanEdit] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const contentRef = useRef("");
  const iconFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!docId) return;
    let on = true;
    api.kbGetDoc(docId).then((d) => {
      if (!on) return;
      setEmoji(d.emoji); setIconUrl(d.icon_url); setTitle(d.title); setScope(d.scope); setCategory(d.category);
      setContent(d.content); contentRef.current = d.content; setCanEdit(d.can_edit); setLoading(false);
    }).catch(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, [docId]);

  async function uploadIcon(files: FileList | null) {
    const f = files?.[0]; if (!f) return;
    try { const r = await api.kbUploadFile(f); setIconUrl(r.url); setEmoji(""); setDirty(true); } catch { /* тихо */ }
    if (iconFileRef.current) iconFileRef.current.value = "";
  }

  async function save(close: boolean) {
    if (saving) return;
    setSaving(true);
    const body = { scope, category: category.trim(), emoji: emoji.trim(), icon_url: iconUrl, title: title.trim(), content: contentRef.current };
    try {
      if (id) await api.kbPatchDoc(id, body);
      else { const d = await api.kbCreateDoc({ ...body, parent_id: parentId }); setId(d.id); }
      setDirty(false);
      onSaved();
      if (close) onClose();
    } catch (e) {
      // Молчать здесь опаснее всего: окно закрывается по requestClose с
      // автосохранением, и при 403/413/обрыве текст статьи пропадал целиком.
      toast.saveFailed("документ", e, () => void save(close));
    } finally { setSaving(false); }
  }
  async function del() {
    // Системные confirm/alert в портале запрещены: они игнорируют тему и шрифт.
    if (!id) return;
    const ok = await confirmDialog({
      title: "Удалить документ?",
      text: "Документ будет удалён без возможности восстановления. Раздел с вложенными документами удалить нельзя.",
      confirmText: "Удалить",
      danger: true,
    });
    if (!ok) return;
    try { await api.kbDeleteDoc(id); onSaved(); onClose(); }
    catch (e) { toast.error(e instanceof Error ? e.message : "Не удалось удалить документ"); }
  }
  function requestClose() {
    if (dirty && canEdit && (title.trim() || contentRef.current)) void save(true);
    else onClose();
  }

  return (
    <>
      <div className="fixed inset-0" style={{ zIndex: Z.MODAL }} onClick={requestClose} />
      <div className="flex flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--surface)] shadow-[0_24px_60px_rgba(0,0,0,0.35)]"
        style={pos} onClick={(e) => e.stopPropagation()}>
        {/* Шапка */}
        <div className="flex shrink-0 items-start gap-2 border-b border-[var(--border)] p-3">
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" disabled={!canEdit} data-tooltip="Иконка документа" style={{ boxShadow: "none" }}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] hover:border-[var(--primary)] disabled:opacity-60">
                {iconUrl ? <AuthImage src={iconUrl} className="h-5 w-5 object-contain" />
                  : emoji ? <span className="text-[18px]">{emoji}</span>
                  : <span className="text-[16px] opacity-40">📄</span>}
              </button>
            </PopoverTrigger>
            {canEdit && (
              <PopoverContent align="start" sideOffset={6} className="w-[268px] space-y-2 p-2.5" style={{ zIndex: Z.DROPDOWN }}>
                <div className="field-label">Иконка документа</div>
                <div className="grid grid-cols-10 gap-0.5">
                  {EMOJI_QUICK.map((e) => (
                    <button key={e} type="button" onClick={() => { setEmoji(e); setIconUrl(""); setDirty(true); }} style={{ boxShadow: "none" }}
                      className="grid h-6 w-6 place-items-center rounded-[var(--radius-md)] text-[16px] hover:bg-[var(--hover)]">{e}</button>
                  ))}
                </div>
                <input value={emoji} onChange={(e) => { setEmoji(e.target.value.slice(0, 12)); setIconUrl(""); setDirty(true); }}
                  placeholder="или вставьте эмодзи 😀"
                  className="field-sm" />
                <div className="flex items-center gap-1.5">
                  <button type="button" onClick={() => iconFileRef.current?.click()}
                    className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border)] px-2 py-1 text-[11px] font-medium text-[var(--textMuted)] hover:border-[var(--primary)] hover:text-[var(--primary)]"><ImageIcon size={12} /> Картинка</button>
                  {(emoji || iconUrl) && (
                    <button type="button" onClick={() => { setEmoji(""); setIconUrl(""); setDirty(true); }}
                      className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border)] px-2 py-1 text-[11px] font-medium text-[var(--textMuted)] hover:border-[#ef4444] hover:text-[#ef4444]">Убрать</button>
                  )}
                </div>
                <input ref={iconFileRef} type="file" accept="image/*" hidden onChange={(e) => void uploadIcon(e.target.files)} />
              </PopoverContent>
            )}
          </Popover>
          <div className="min-w-0 flex-1">
            <input value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} disabled={!canEdit}
              placeholder="Заголовок документа" 
              className="field bg-transparent text-[18px] font-bold" />
            {isChild ? (
              <div className="mt-1 text-[11px] text-[var(--textMuted)]">Вложенный документ — область и раздел наследуются от родителя</div>
            ) : (
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {/* Область */}
              <span className="seg-group">
                {(["personal", "team"] as KbDocScope[]).map((s) => (
                  <button key={s} type="button" disabled={!canEdit} onClick={() => { setScope(s); setDirty(true); }}
                    style={{ boxShadow: "none" }}
                    className={`px-2 py-0.5 text-[11px] font-medium transition ${scope === s ? "seg seg-on" : "seg"}`}>
                    {s === "personal" ? "Личное" : "Командное"}
                  </button>
                ))}
              </span>
              {/* Раздел — элементом портала, не системным списком браузера
                  (та же правка, что на экране базы знаний, 02.09.2026). */}
              <ВыборРаздела value={category} options={categories}
                onChange={(v) => { setCategory(v); setDirty(true); }} />
            </div>
            )}
          </div>
          <Button iconOnly onClick={requestClose}><IconClose size={16} /></Button>
        </div>

        {/* Тело-редактор */}
        <div className="min-h-0 flex-1">
          {loading ? (
            <div className="flex h-full items-center justify-center text-[var(--textMuted)]"><IconSpinner className="animate-spin" size={20} /></div>
          ) : (
            <RichEditor value={content} editable={canEdit} onChange={(html) => { contentRef.current = html; setDirty(true); }} />
          )}
        </div>

        {/* Низ */}
        {canEdit && (
          <div className="flex shrink-0 items-center gap-2 border-t border-[var(--border)] px-3 py-2">
            <Button variant="primary" onClick={() => void save(false)} disabled={saving || !title.trim()}>
              {saving ? "Сохранение…" : "Сохранить"}
            </Button>
            <span className="text-[11px] text-[var(--textMuted)]">{dirty ? "есть несохранённые изменения" : "сохранено"}</span>
            <span className="flex-1" />
            {id && (
              <Button title="Удалить документ" onClick={() => void del()}><IconTrash size={12} /> Удалить</Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
